import React, { useState, useEffect } from 'react';
import ChristmasWidgets from '../components/ChristmasWidgets'; // adjust path
import axios from 'axios';
import config from '../config/config';

const ChristmasWidgetsAdmin = ({ slug = '', onSave }) => {
  const defaultState = {
    preset: 'design1',
    title: 'Holiday Sale',
    subtitle: 'Up to 70% Off Everything',
    buttonText: 'Shop Now',
    countdownDate: '2026-12-25T00:00:00',
    showSnow: true,
    showStars: true,
    devMode: false,
    customStyles: {
      background: 'bg-gradient-to-br from-red-900 via-red-700 to-green-900',
      textColor: '#ffffff',
      accentColor: '#fbbf24',
      buttonBg: '#ffffff',
      buttonText: '#991b1b',
    },
  };

  const [preset, setPreset] = useState(defaultState.preset);
  const [title, setTitle] = useState(defaultState.title);
  const [subtitle, setSubtitle] = useState(defaultState.subtitle);
  const [buttonText, setButtonText] = useState(defaultState.buttonText);
  const [countdownDate, setCountdownDate] = useState(defaultState.countdownDate);
  const [showSnow, setShowSnow] = useState(defaultState.showSnow);
  const [showStars, setShowStars] = useState(defaultState.showStars);
  const [devMode, setDevMode] = useState(defaultState.devMode);
  const [customStyles, setCustomStyles] = useState(defaultState.customStyles);

  const [fullScreenPreview, setFullScreenPreview] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    const loadSettings = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

        let response;
        const isDuplicate = slug.includes('-');

        if (isDuplicate) {
          response = await axios.get(
            `${config.API_URL}/api/pullCustomSettings/${slug}`,
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } else {
          response = await axios.get(
            `${config.API_URL}/api/pullCustomSettings/christmas-widgets`, // or your endpoint
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        }

        const dbData = isDuplicate
          ? response.data.customSettings || {}
          : response.data.settings || {};

        if (dbData.preset) setPreset(dbData.preset);
        if (dbData.title) setTitle(dbData.title);
        if (dbData.subtitle) setSubtitle(dbData.subtitle);
        if (dbData.buttonText) setButtonText(dbData.buttonText);
        if (dbData.countdownDate) setCountdownDate(dbData.countdownDate);
        if (typeof dbData.showSnow === 'boolean') setShowSnow(dbData.showSnow);
        if (typeof dbData.showStars === 'boolean') setShowStars(dbData.showStars);
        if (typeof dbData.devMode === 'boolean') setDevMode(dbData.devMode);
        if (dbData.customStyles) setCustomStyles(dbData.customStyles);
      } catch (error) {
        console.error('Failed to load Christmas widgets settings:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [token, slug]);

  const currentConfig = {
    preset,
    title,
    subtitle,
    buttonText,
    countdownDate,
    showSnow,
    showStars,
    customStyles,
    devMode,
  };

  const saveSettings = async () => {
    if (!token) {
      alert('Please log in first!');
      return;
    }

    try {
      setSaving(true);
      await axios.get(`${config.API_URL}/sanctum/csrf-cookie`, { withCredentials: true });

      const xsrfToken = document.cookie
        .split('; ')
        .find((row) => row.startsWith('XSRF-TOKEN='))
        ?.split('=')[1];

       const endpoint = slug ? `/api/saveChristmasSettings/${slug}` : '/api/saveChristmasSettings';

      const response = await axios.post(
        `${config.API_URL}${endpoint}`,
        { settings: currentConfig },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-XSRF-TOKEN': decodeURIComponent(xsrfToken || ''),
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      if (response.data.status === 1) {
        alert('Christmas Widget saved successfully! 🎄');
        if (onSave) onSave(currentConfig);
      } else {
        alert('Save failed.');
      }
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <p className="text-xl text-gray-600">Loading your Christmas widget config...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 flex">
      {/* Preview */}
      <div className="flex-1 relative">
        <div className="absolute inset-0 flex flex-col">
          <div className="bg-white border-b px-6 py-4 flex justify-between items-center shadow-sm">
            <h1 className="text-2xl font-bold text-gray-900">Christmas Widget Editor</h1>
            <button
              onClick={() => setFullScreenPreview(true)}
              className="bg-red-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-red-700"
            >
              Full Screen Preview
            </button>
          </div>
          <div className="flex-1 overflow-auto bg-gray-50 p-8">
            <ChristmasWidgets config={currentConfig} />
          </div>
        </div>
      </div>

      {/* Settings Panel */}
      <div className="w-96 bg-white border-l overflow-y-auto shadow-2xl">
        <div className="p-6 space-y-6 pb-40">
          {/* Preset */}
          <div>
            <h3 className="font-medium mb-3">Widget Design</h3>
            <div className="grid gap-3">
              {['design1', 'design2', 'design3'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPreset(p)}
                  className={`p-4 border rounded-lg text-left ${
                    preset === p ? 'border-red-500 bg-red-50' : 'border-gray-200'
                  }`}
                >
                  <span className="font-medium capitalize">Design {p.slice(-1)}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Main Content */}
          <div className="space-y-3">
            <h3 className="font-medium">Main Texts</h3>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-4 py-2 border rounded-md"
              placeholder="Title (e.g. Holiday Sale)"
            />
            <input
              value={subtitle}
              onChange={(e) => setSubtitle(e.target.value)}
              className="w-full px-4 py-2 border rounded-md"
              placeholder="Subtitle"
            />
            <input
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              className="w-full px-4 py-2 border rounded-md"
              placeholder="Button Text"
            />
          </div>

          {/* Countdown */}
          <div className="space-y-3">
            <h3 className="font-medium">Countdown Date</h3>
            <input
              type="datetime-local"
              value={countdownDate.slice(0, 16)}
              onChange={(e) => setCountdownDate(e.target.value + ':00')}
              className="w-full px-4 py-2 border rounded-md"
            />
          </div>

          {/* Effects */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-medium">Show Snowflakes</span>
              <input
                type="checkbox"
                checked={showSnow}
                onChange={(e) => setShowSnow(e.target.checked)}
                className="w-5 h-5 text-red-600 rounded"
              />
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium">Show Twinkling Stars</span>
              <input
                type="checkbox"
                checked={showStars}
                onChange={(e) => setShowStars(e.target.checked)}
                className="w-5 h-5 text-red-600 rounded"
              />
            </div>
          </div>

          {/* Dev Mode */}
          <div className="bg-red-50 border border-red-200 rounded-xl p-5">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-semibold text-red-900">Dev Mode – Custom Styles</h3>
              <button
                onClick={() => setDevMode(!devMode)}
                className={`relative inline-flex h-6 w-11 rounded-full transition-colors ${
                  devMode ? 'bg-red-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 rounded-full bg-white transition ${
                    devMode ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {devMode && (
              <div className="space-y-4 text-sm">
                <div>
                  <label className="block font-medium mb-1">Background (Tailwind / gradient / url)</label>
                  <input
                    value={customStyles.background}
                    onChange={(e) =>
                      setCustomStyles({ ...customStyles, background: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="bg-gradient-to-br from-red-900 to-green-900"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Text Color</label>
                  <input
                    value={customStyles.textColor}
                    onChange={(e) =>
                      setCustomStyles({ ...customStyles, textColor: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="#ffffff"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Accent Color (buttons, highlights)</label>
                  <input
                    value={customStyles.accentColor}
                    onChange={(e) =>
                      setCustomStyles({ ...customStyles, accentColor: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="#fbbf24"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Button Background</label>
                  <input
                    value={customStyles.buttonBg}
                    onChange={(e) =>
                      setCustomStyles({ ...customStyles, buttonBg: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="#ffffff"
                  />
                </div>
                <div>
                  <label className="block font-medium mb-1">Button Text Color</label>
                  <input
                    value={customStyles.buttonText}
                    onChange={(e) =>
                      setCustomStyles({ ...customStyles, buttonText: e.target.value })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                    placeholder="#991b1b"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Fixed Save Button */}
        <div className="fixed bottom-0 right-0 w-96 bg-white border-t p-6 shadow-lg">
          <button
            onClick={saveSettings}
            disabled={saving}
            className="w-full bg-red-600 text-white py-4 rounded-xl font-bold hover:bg-red-700 disabled:opacity-60 shadow"
          >
            {saving ? 'Saving...' : 'Save Christmas Widget'}
          </button>
        </div>
      </div>

      {/* Fullscreen Preview */}
      {fullScreenPreview && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex flex-col"
          onClick={() => setFullScreenPreview(false)}
        >
          <div className="bg-white/10 backdrop-blur px-6 py-4 flex justify-end">
            <button onClick={() => setFullScreenPreview(false)} className="text-white text-5xl hover:text-gray-300">
              ×
            </button>
          </div>
          <div className="flex-1 overflow-auto p-8" onClick={(e) => e.stopPropagation()}>
            <ChristmasWidgets config={currentConfig} />
          </div>
        </div>
      )}
    </div>
  );
};

export default ChristmasWidgetsAdmin;